import { NavLink } from "./NavLink";
import { SignOutButton } from "@clerk/react";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";

import { AccountBootstrapController, type AccountConnectionState } from "../auth/accountBootstrap";
import { useSession } from "../auth/session";
import { Button } from "./Button";
import { StatusChip } from "./StatusChip";

interface AccountStatusValue {
  accountId: string | null;
  canRetry: boolean;
  connectionState: AccountConnectionState;
  label: string;
  description?: string;
  retry(): void;
  signOutAction: ReactNode;
}

const AccountStatusContext = createContext<AccountStatusValue | null>(null);

function AccountSignOut({ compact = false }: { compact?: boolean }) {
  const { state } = useSession();
  if (state !== "signed-in") return null;
  return <SignOutButton redirectUrl="/login">
    <Button variant="quiet" className={compact ? "sbx-action sbx-action--quiet" : ""}>Sign out</Button>
  </SignOutButton>;
}

export function AccountStatusProvider({ children, gateRoutes = false, showWhileConnecting = false, connectingView }: { children: ReactNode; gateRoutes?: boolean; showWhileConnecting?: boolean; connectingView?: ReactNode | ((state: AccountConnectionState) => ReactNode) }) {
  const { getToken, sessionId, state: sessionState } = useSession();
  const [connectionState, setConnectionState] = useState<AccountConnectionState>("clerk-loading");
  const controllerRef = useRef<AccountBootstrapController | null>(null);
  controllerRef.current ??= new AccountBootstrapController(setConnectionState);
  controllerRef.current.setListener(setConnectionState);

  useEffect(() => {
    controllerRef.current?.sync(
      { sessionId, state: sessionState },
      getToken,
      (token, signal) => fetch("/api/bootstrap", {
        headers: { authorization: `Bearer ${token}` },
        signal,
      }),
    );
  }, [getToken, sessionId, sessionState]);

  useEffect(() => () => controllerRef.current?.dispose(), []);

  const label =
    sessionState === "config-missing"
      ? "Auth setup required"
      : connectionState === "clerk-loading"
        ? "Loading session"
        : connectionState === "signed-out"
          ? "Signed out"
          : connectionState === "acquiring-session"
            ? "Acquiring session"
            : connectionState === "connecting-account"
              ? "Connecting account"
              : connectionState === "workspace-ready"
                ? "Workspace ready"
                : connectionState === "authentication-error"
                  ? "Authentication failed"
                  : "Account connection failed";

  // sync stores a retry operation only for a signed-in session with a usable ID.
  const canRetry = sessionState === "signed-in" && Boolean(sessionId) &&
    (connectionState === "authentication-error" || connectionState === "bootstrap-error");
  const description = connectionState === "authentication-error"
    ? sessionId
      ? "We couldn't verify your session. Retry, or sign out and sign in again."
      : "Your session is unavailable. Sign out and sign in again to reconnect."
    : connectionState === "bootstrap-error"
      ? "We couldn't connect your SignalBill account. Retry, or sign out and sign in again."
      : undefined;

  return (
    <AccountStatusContext.Provider value={{
      accountId: connectionState === "workspace-ready" ? controllerRef.current?.accountId ?? null : null,
      canRetry,
      connectionState,
      label,
      description,
      retry: () => controllerRef.current?.retry(),
      signOutAction: <AccountSignOut />,
    }}>
      {!gateRoutes || sessionState === "config-missing" || connectionState === "workspace-ready" ||
        (showWhileConnecting && (sessionState === "loading" || sessionState === "signed-in") &&
          connectionState !== "authentication-error" && connectionState !== "bootstrap-error" && connectionState !== "signed-out")
        ? children : typeof connectingView === "function" ? connectingView(connectionState) : connectingView ?? <AccountStatus />}
    </AccountStatusContext.Provider>
  );
}

export function AccountStatusView({ canRetry, label, description, onRetry, signOutAction, compact = false }: {
  canRetry: boolean;
  label: string;
  description?: string;
  onRetry(): void;
  signOutAction?: ReactNode;
  compact?: boolean;
}) {
  return (
    <section className={`account-status${compact ? " account-status--compact" : ""}`} aria-label="Workspace account" aria-live="polite">
      {compact ? <p className="auth-status">{description ?? label}</p> : <div className="account-status-identity">
        <span className="workspace-avatar" aria-hidden="true">SB</span>
        <span className="account-status-copy">
          <strong>SignalBill</strong>
          <StatusChip tone={canRetry ? "warning" : "neutral"}>{label}</StatusChip>
        </span>
      </div>}
      {!compact && description ? <p className="auth-status">{description}</p> : null}
      <div className="account-status-actions">
        {canRetry ? <Button variant="quiet" className={compact ? "sbx-action sbx-action--quiet" : ""} onClick={onRetry}>Retry</Button> : null}
        {signOutAction}
      </div>
    </section>
  );
}

export function AccountStatus({ compact = false }: { compact?: boolean }) {
  const value = useContext(AccountStatusContext);
  if (!value) throw new Error("AccountStatus must be rendered inside AccountStatusProvider.");
  return <AccountStatusView {...value} compact={compact} signOutAction={<>{value.connectionState === "workspace-ready" ? <NavLink href="/settings/billing" active={false}>Subscription billing</NavLink> : null}{compact ? <AccountSignOut compact /> : value.signOutAction}</>} onRetry={value.retry} />;
}

export function useAccountConnectionState(): AccountConnectionState {
  const value = useContext(AccountStatusContext);
  if (!value) throw new Error("AccountStatus must be rendered inside AccountStatusProvider.");
  return value.connectionState;
}

export function useWorkspaceAccountId(): string | null {
  return useContext(AccountStatusContext)?.accountId ?? null;
}
